import { parseWorkbookSheet, type XlsxParseRequest, type XlsxParseResponse } from "./workbookSheet";

/**
 * Parses a workbook, off the main thread when the platform allows (#6892).
 *
 * ## Why this wrapper exists rather than calling the worker directly
 *
 * Two environments cannot run one:
 *
 *  - jsdom, so every existing test of the preview would otherwise need a
 *    Worker polyfill to exercise a code path that is not what it is testing.
 *  - any browser where the worker fails to construct (a CSP that blocks
 *    blob/module workers, for instance).
 *
 * In both, this falls back to parsing inline. The fallback is the OLD
 * behaviour — a frozen tab for the duration — which is worse than the worker
 * and better than a preview that does not load. It is a graceful degradation,
 * not a silent one: `parsedOffThread` reports which path ran so a caller can
 * disclose it if it ever matters.
 */

export interface ParsedWorkbook {
  sheetName: string;
  rows: unknown[][];
  /** False when the platform forced the main-thread fallback. */
  parsedOffThread: boolean;
}

/**
 * The original synchronous path, kept as the fallback.
 *
 * Blocks the calling thread for the duration — 4.35 s on the 497,711-row
 * fixture — so it must only ever run where a worker is unavailable.
 */
export const parseWorkbookInline = (buffer: ArrayBuffer, processSheetName?: string): ParsedWorkbook => {
  const { sheetName, rows } = parseWorkbookSheet(buffer, processSheetName);
  return { sheetName, rows, parsedOffThread: false };
};

/** True when this platform can run a module worker at all. */
export const canUseWorker = (): boolean => typeof Worker !== "undefined";

export const parseWorkbook = async (
  buffer: ArrayBuffer,
  processSheetName?: string,
  signal?: AbortSignal
): Promise<ParsedWorkbook> => {
  if (!canUseWorker()) {
    return parseWorkbookInline(buffer, processSheetName);
  }

  let worker: Worker;
  try {
    worker = new Worker(new URL("@/workers/xlsxParse.worker.ts", import.meta.url), { type: "module" });
  } catch {
    // Construction itself failed (CSP, unsupported module workers). Parsing
    // inline is worse for the user than the worker and better than no
    // preview at all.
    return parseWorkbookInline(buffer, processSheetName);
  }

  try {
    return await new Promise<ParsedWorkbook>((resolve, reject) => {
      const onAbort = () => {
        // The drawer closed or the document changed. Rejecting is enough to
        // stop the parse: the `finally` below terminates the worker on every
        // exit path, so a 4-second parse cannot keep running into a promise
        // nobody awaits — the CPU this change exists to give back.
        //
        // An explicit terminate() here was redundant, and provably so: the
        // abort test still passed with it removed, which is how it was found.
        reject(new DOMException("Aborted", "AbortError"));
      };
      signal?.addEventListener("abort", onAbort, { once: true });

      worker.onmessage = (event: MessageEvent<XlsxParseResponse>) => {
        const data = event.data;
        if (data.ok) {
          resolve({ sheetName: data.sheetName, rows: data.rows, parsedOffThread: true });
        } else {
          reject(new Error(data.error));
        }
      };

      // An `error` event is a worker that failed to load or threw outside the
      // handler.
      //
      // We CANNOT fall back here even if we wanted to: the buffer was
      // TRANSFERRED at the postMessage below and is now detached, so an
      // inline reparse would read zero bytes. This is a hard constraint, not
      // a UX trade — an earlier version of this comment argued the freeze
      // would happen twice, which invites exactly the change it means to
      // prevent: someone reads "we chose not to fall back" and adds a
      // fallback for robustness, getting an empty preview instead.
      //
      // The recovery path is the caller re-fetching. useSourcePreview caches
      // the parsed RESULT, not the buffer, so a retry gets fresh bytes.
      worker.onerror = () => reject(new Error("Failed to parse spreadsheet"));

      const request: XlsxParseRequest = { buffer, processSheetName };
      // The buffer is TRANSFERRED, not copied — a 12.9 MB structured clone on
      // the main thread would reintroduce a chunk of the pause being removed.
      // The caller must not read `buffer` after this call; it does not.
      worker.postMessage(request, [buffer]);
    });
  } finally {
    worker.terminate();
  }
};
